/*
 * File: abbyCadabbyApi.ts
 * Project: rosita
 * -----
 * Last Modified: 03 Mar 2022 12:22:31 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { BaseQueryApi, BaseQueryFn } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import { isEqual } from 'lodash';
import { ApiMultipleResponse } from 'models/ApiModels';
import { POReceiptModel } from 'models/POReceiptModel';
import { POReceiptParamsModel } from 'models/POReceiptParamsModel';
import { ReduxState } from 'redux/store';
import { GrannyBirdBinParams } from './binParams';

const baseUrl = process.env.REACT_APP_GRANNY_BIRD_BASE_URL;
const functionsKey = process.env.REACT_APP_GRANNY_BIRD_FUNCTION_KEY;

const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = (args: string | FetchArgs, api: BaseQueryApi, extraOptions: any) => {
  const { selectedLegalEntity } = (api.getState() as ReduxState).selectedLegalEntity;

  return fetchBaseQuery({
    baseUrl: `${baseUrl}/${selectedLegalEntity}`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
        headers.set('Content-Type', 'application/json');
      }

      return headers;
    }
  })(args, api, extraOptions);
};

export const grannyBirdApi = createApi({
  reducerPath: 'grannyBirdApi',
  baseQuery,
  tagTypes: ['PurchaseOrder', 'PurchaseOrders', 'BinLocations'],
  endpoints: (builder) => ({
    getPurchaseOrders: builder.query<ApiMultipleResponse<POReceiptModel>, { params: POReceiptParamsModel }>({
      query: ({ params }) => ({
        url: 'purchaseOrders',
        params
      }),
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().grannyBirdApi;

          if (originalArgs?.params !== undefined) {
            const { offset } = originalArgs?.params;
            const lastQueryArgs = originalArgs ? { ...originalArgs, params: { ...originalArgs.params, offset: offset - 25 } } : undefined;
            const lastQueryData = Object.values(queries).find((value) => isEqual(value?.originalArgs, lastQueryArgs));

            if (lastQueryData) {
              _api.updateCachedData((draft: any) => {
                draft.data.unshift(...(lastQueryData.data as any).data);
              });
            }
          }
        } catch (error) {
          console.log(error, '<= error');
          //
        }
      },
      providesTags: ['PurchaseOrders']
    }),
    getBinLocations: builder.query<ApiMultipleResponse<string>, { params: GrannyBirdBinParams; warehouseId: string }>({
      query: ({ params, warehouseId }) => ({
        url: `/warehouses/${warehouseId}/locations`,
        params
      }),
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().grannyBirdApi;

          if (originalArgs?.params !== undefined) {
            const { offset } = originalArgs?.params;
            const lastQueryArgs = originalArgs ? { ...originalArgs, params: { ...originalArgs.params, offset: offset - 25 } } : undefined;
            const lastQueryData = Object.values(queries).find((value) => isEqual(value?.originalArgs, lastQueryArgs));

            if (lastQueryData) {
              _api.updateCachedData((draft: any) => {
                draft.data.unshift(...(lastQueryData.data as any).data);
              });
            }
          }
        } catch (error) {
          console.log(error, '<= error');
          //
        }
      },
      providesTags: ['BinLocations']
    }),
    getPurchaseOrder: builder.query<POReceiptModel, { purchaseOrderId: string }>({
      query: ({ purchaseOrderId }) => ({
        url: `purchaseOrders/${purchaseOrderId}/lineNumbers`
      }),
      providesTags: ['PurchaseOrder']
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const grannyBirdInfo = createApi({
  reducerPath: 'grannyBirdInfo',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
        headers.set('Content-Type', 'application/json');
      }

      return headers;
    }
  }),
  endpoints: (builder) => ({
    getVersion: builder.query<string, void>({
      query: () => ({
        baseUrl: baseUrl,
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        baseUrl: baseUrl,
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const { useGetPurchaseOrdersQuery, useGetPurchaseOrderQuery, useGetBinLocationsQuery } = grannyBirdApi;
export const { useGetApiNameQuery, useGetVersionQuery } = grannyBirdInfo;
export const grannyBirdReducer = grannyBirdApi.reducer;
